/**
 * Partner Portal — Marketing Resources (Slice E1 — Feb 2026 July).
 *
 * Surfaces the existing PWS × AWARE marketing kit for the franchisee:
 *   • Personal booking link + one-click copy
 *   • QR code (PNG + SVG downloads + a preview img)
 *   • Printable one-page A4 flyer (opens in new tab, browser prints to PDF)
 *   • Partner social bio blurb + suggested captions
 *
 * All data is fetched from `GET /api/partner/pws-marketing-kit`.
 * The partner-specific flyer + QR endpoints already exist on the
 * backend (see `/api/partner/pws-marketing-kit/flyer.html` +
 * `/api/partner/pws-marketing-kit/qr-code.{png,svg}`), so this page
 * is a thin UI over them.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Copy, Check, Download, ExternalLink, Printer,
  QrCode, Share2, FileText, Sparkles, AlertTriangle, Camera, Upload,
  FlaskConical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { PartnerShell } from "@/pages/partner/Dashboard";
import { fetchAndOpenAuthed } from "@/lib/authedDownload";
import { EmailSignatureBannerCard } from "@/components/partner/EmailSignatureBannerCard";
import { FacebookPostKitCard } from "@/components/partner/FacebookPostKitCard";


export default function PartnerMarketingResourcesPage() {
  useSEO({ title: "Marketing & Referrals — Aware Partner Portal", noindex: true, includeBaseline: false });

  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLabel, setCopiedLabel] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partner/pws-marketing-kit");
        setKit(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Unable to load marketing kit.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLabel(label);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedLabel(null), 1500);
    } catch {
      toast.error("Couldn't copy — please select the text manually.");
    }
  };

  if (loading) {
    return (
      <PartnerShell active="marketing & referrals">
        <div className="p-8 flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading your marketing kit…
        </div>
      </PartnerShell>
    );
  }

  if (error) {
    return (
      <PartnerShell active="marketing & referrals">
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-4 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" /> {error}
        </div>
      </PartnerShell>
    );
  }

  const partner = kit?.partner || {};
  const booking = kit?.booking_link_card || {};
  const social = kit?.social_posts || [];
  const brand = kit?.brand_disclosure_block || {};

  // Partner-scoped endpoints (server enforces auth = this partner only).
  // Auth-required file endpoints are downloaded via `fetchAndOpenAuthed`
  // so the Bearer token from `api` interceptor is attached — a plain
  // `<a href>` would trigger a native browser download without the
  // header and get a 401 "Not authenticated" (bug reported Feb 2026).
  const slug = partner.partner_slug || "flyer";
  const openFlyerPdf   = () => fetchAndOpenAuthed({
    path: "/partner/pws-marketing-kit/flyer.pdf",
    mode: "download",
    filename: `pws-flyer-${slug}.pdf`,
    loadingMessage: "Preparing your flyer PDF…",
  });
  const openFlyerHtml  = () => fetchAndOpenAuthed({
    path: "/partner/pws-marketing-kit/flyer.html",
    mode: "open",
    loadingMessage: "Loading flyer…",
  });
  const downloadQrPng  = () => fetchAndOpenAuthed({
    path: "/partner/pws-marketing-kit/qr-code.png",
    mode: "download",
    filename: `pws-${slug}.png`,
  });
  const downloadQrSvg  = () => fetchAndOpenAuthed({
    path: "/partner/pws-marketing-kit/qr-code.svg",
    mode: "download",
    filename: `pws-${slug}.svg`,
  });
  const qrPngDataUrl = booking?.qr_code?.png_data_url;

  // Auto-derive a social bio blurb — respects the calm/factual PWS tone.
  const bioBlurb =
    `${partner.business_name || "Pristine Water Systems"} — your local partner for professional water testing across ${partner.franchise_region || partner.state || "Queensland"}. ` +
    `Book online or scan the QR: ${booking.booking_url || ""}`;

  return (
    <PartnerShell active="marketing & referrals">
      <div className="space-y-6" data-testid="partner-marketing-page">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Marketing</div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">Marketing &amp; Referrals</h1>
          <p data-testid="partner-marketing-subtext" className="mt-2 text-base text-slate-600 max-w-2xl">
            Download banners, graphics and ready-to-use material to help promote AWARE to your customers.
          </p>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            All approved, on-brand assets for your PWS × AWARE partnership. Everything here is
            personalised to <b>{partner.business_name || "your franchise"}</b> — every booking link, QR code
            and flyer routes water tests directly back to you.
          </p>
        </div>

        <Link
          to="/partner/test-hub"
          data-testid="partner-marketing-crosslink-tools"
          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-cyan-300 hover:bg-cyan-50/40 transition-colors"
        >
          <span className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">
              Looking for your AWARE landing page, referral link or QR code?
            </span>{" "}
            Go to AWARE Tools &amp; Links.
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 whitespace-nowrap">
            <FlaskConical className="w-4 h-4" /> Open
          </span>
        </Link>

        {/* Jun 2026 — AWARE email signature banner */}
        <EmailSignatureBannerCard
          sig={kit?.email_signature}
          testidPrefix="partner-email-signature"
        />

        {/* Jun 2026 — Facebook Community Post Kit */}
        <FacebookPostKitCard
          kit={kit?.facebook_post}
          testidPrefix="partner-facebook-post"
        />

        {/* Booking link card */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="partner-marketing-booking-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
              <Share2 className="w-4 h-4 text-cyan-600" /> Your booking link
            </h2>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Attribution locked to your franchise</span>
          </div>
          <div className="flex gap-2 items-stretch">
            <div className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-800 truncate" data-testid="partner-marketing-booking-url">
              {booking.booking_url || "Not available"}
            </div>
            <Button
              data-testid="partner-marketing-copy-booking"
              onClick={() => copy(booking.booking_url || "", "booking")}
              disabled={!booking.booking_url}
              className="bg-slate-900 hover:bg-cyan-700 text-white"
            >
              {copiedLabel === "booking" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copiedLabel === "booking" ? "Copied" : "Copy"}
            </Button>
            {booking.booking_url && (
              <a
                href={booking.booking_url}
                target="_blank"
                rel="noreferrer"
                data-testid="partner-marketing-open-booking"
                className="inline-flex items-center px-3 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm"
              >
                <ExternalLink className="w-4 h-4 mr-1" /> Preview
              </a>
            )}
          </div>
          <p className="text-[11px] text-slate-500">
            Every water test booked through this link is automatically attributed to you for commission tracking.
          </p>
        </section>

        {/* Feb 2026 — Landing page URL card. The landing page is
            auto-created in DRAFT on partner activation, and
            auto-published on the partner's first headshot upload. */}
        <PartnerLandingLinkCard partner={partner} copiedLabel={copiedLabel} copy={copy} />

        {/* Feb 2026 — Partner self-serve editor for the region label
            and hero intro copy. Admin still controls status, phone,
            email, and franchisee_name. */}
        <PartnerLandingContentCard partner={partner} />

        {/* Feb 2026 — Franchisee headshot upload. Populates the round
            photo on the customer-facing partner landing page. */}
        <FranchiseeHeadshotCard partner={partner} />

        {/* QR code + Flyer side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* QR code */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="partner-marketing-qr-card">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
                <QrCode className="w-4 h-4 text-cyan-600" /> QR code
              </h2>
            </div>
            <div className="mt-4 flex items-center gap-5">
              {qrPngDataUrl ? (
                <img
                  data-testid="partner-marketing-qr-image"
                  src={qrPngDataUrl}
                  alt="Your PWS booking QR code"
                  className="w-36 h-36 rounded-lg border border-slate-200 bg-white p-2 flex-none"
                />
              ) : (
                <div className="w-36 h-36 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs text-center px-3">
                  QR unavailable — partner slug not set
                </div>
              )}
              <div className="min-w-0 space-y-2 text-sm">
                <p className="text-slate-600">Print onto flyers, business cards, van signage — every scan goes to your booking link.</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={downloadQrPng}
                    data-testid="partner-marketing-qr-png-btn"
                    className="inline-flex items-center gap-1 text-xs font-medium border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 text-slate-800 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> PNG
                  </button>
                  <button
                    type="button"
                    onClick={downloadQrSvg}
                    data-testid="partner-marketing-qr-svg-btn"
                    className="inline-flex items-center gap-1 text-xs font-medium border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 text-slate-800 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> SVG (print quality)
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Flyer card */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="partner-marketing-flyer-card">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-600" /> One-page A4 flyer
              </h2>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                Personalised
              </span>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Approved PWS &times; AWARE flyer with your business name, phone, region and QR code baked in.
              Download the native A4 PDF for print-ready quality, or open the HTML version to preview.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openFlyerPdf}
                data-testid="partner-marketing-flyer-pdf"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-cyan-700 text-white text-sm font-medium rounded-md px-4 py-2 transition-colors"
              >
                <Download className="w-4 h-4" /> Download PDF (A4)
              </button>
              <button
                type="button"
                onClick={openFlyerHtml}
                data-testid="partner-marketing-flyer-open"
                className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-800 text-sm font-medium rounded-md px-4 py-2 transition-colors"
              >
                <ExternalLink className="w-4 h-4" /> Open HTML flyer
              </button>
              <button
                type="button"
                onClick={async () => {
                  // Fetch as authenticated blob, open in a new tab, then
                  // auto-trigger the browser print dialog.
                  const ok = await fetchAndOpenAuthed({
                    path: "/partner/pws-marketing-kit/flyer.html",
                    mode: "open",
                    loadingMessage: "Opening flyer for print…",
                  });
                  if (!ok) return;
                  // The opened tab has its own window; there's no reliable
                  // way to trigger print inside it from here (cross-origin
                  // policy on blob:). Toast a hint instead.
                  setTimeout(
                    () => toast("Tip: in the new tab, press Ctrl/⌘ + P → Save as PDF"),
                    600,
                  );
                }}
                data-testid="partner-marketing-flyer-print"
                className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-800 text-sm font-medium rounded-md px-4 py-2 transition-colors"
              >
                <Printer className="w-4 h-4" /> Print (browser)
              </button>
            </div>
          </section>
        </div>

        {/* Social bio + suggested captions */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="partner-marketing-social">
          <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cyan-600" /> Social bio + captions
          </h2>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-600">Social bio blurb</label>
              <Button
                variant="outline"
                data-testid="partner-marketing-copy-bio"
                onClick={() => copy(bioBlurb, "bio")}
                className="h-7 text-xs"
              >
                {copiedLabel === "bio" ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                {copiedLabel === "bio" ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 whitespace-pre-wrap">{bioBlurb}</div>
          </div>

          {social.length > 0 && (
            <div className="space-y-3">
              <label className="text-xs font-medium text-slate-600">Suggested captions</label>
              {social.slice(0, 3).map((post, i) => {
                const body = post.body || post.text || post.content || post.caption || "";
                if (!body) return null;
                const label = post.channel || post.platform || post.name || `Post ${i + 1}`;
                return (
                  <div key={i} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`partner-marketing-social-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-cyan-700">{label}</span>
                      <Button
                        variant="outline"
                        data-testid={`partner-marketing-copy-social-${i}`}
                        onClick={() => copy(body, `social-${i}`)}
                        className="h-6 text-[11px] px-2"
                      >
                        {copiedLabel === `social-${i}` ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copiedLabel === `social-${i}` ? "Copied" : "Copy"}
                      </Button>
                    </div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{body}</div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Property Manager program resources (moved off the
            /partner/marketing-resources path — it previously shadowed
            this page via a duplicate route). Kept reachable here. */}
        <Link
          to="/partner/pm-program-resources"
          data-testid="partner-marketing-pm-program-link"
          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-cyan-300 hover:bg-cyan-50/40 transition-colors"
        >
          <span className="text-sm text-slate-700">
            <span className="font-semibold text-slate-900">
              AWARE Property Manager program resources
            </span>{" "}
            — partner-branded material for introducing the program to agencies.
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 whitespace-nowrap">
            <FileText className="w-4 h-4" /> Open
          </span>
        </Link>

        {/* Compliance footer */}
        {kit?.compliance_note && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-500" />
            <span>{kit.compliance_note}</span>
          </div>
        )}
      </div>
    </PartnerShell>
  );
}


// ── Customer landing page URL ─────────────────────────────────────
// Auto-created on partner activation, auto-published on first
// headshot upload. This card just exposes the URL so the partner
// can copy / preview / share it — never a form.
function PartnerLandingLinkCard({ partner, copiedLabel, copy }) {
  const [page, setPage] = useState(null);
  useEffect(() => {
    if (!partner?.partner_slug) return;
    let cancelled = false;
    api.get(`/pws-partner-landing/by-partner-slug/${encodeURIComponent(partner.partner_slug)}`)
      .then(({ data }) => { if (!cancelled) setPage(data); })
      .catch(() => { if (!cancelled) setPage(null); });
    return () => { cancelled = true; };
  }, [partner?.partner_slug]);

  // Prefer the landing-page slug (SEO-friendly) but fall back to the
  // partner attribution slug so the URL is still valid before the
  // first admin approval.
  const slug = page?.slug || partner?.partner_slug;
  const baseUrl =
    (typeof window !== "undefined" && window.location.origin) ||
    "";
  const landingUrl = slug ? `${baseUrl}/pws-partner/${encodeURIComponent(slug)}` : "";
  const isPublished = page?.status === "published";

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
      data-testid="partner-marketing-landing-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
          <ExternalLink className="w-4 h-4 text-cyan-600" /> Your customer landing page
        </h2>
        <span
          data-testid="partner-marketing-landing-status"
          className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
            isPublished
              ? "bg-emerald-100 text-emerald-800"
              : page?.status === "draft"
                ? "bg-amber-100 text-amber-800"
                : "bg-slate-100 text-slate-600"
          }`}
        >
          {isPublished ? "Live" : page?.status === "draft" ? "Draft — publishes on first headshot" : "Awaiting activation"}
        </span>
      </div>
      <div className="flex gap-2 items-stretch">
        <div
          className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-800 truncate"
          data-testid="partner-marketing-landing-url"
        >
          {landingUrl || "Not available yet"}
        </div>
        <Button
          data-testid="partner-marketing-copy-landing"
          onClick={() => copy(landingUrl, "landing")}
          disabled={!landingUrl}
          className="bg-slate-900 hover:bg-cyan-700 text-white"
        >
          {copiedLabel === "landing" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copiedLabel === "landing" ? "Copied" : "Copy"}
        </Button>
        {landingUrl && (
          <a
            href={landingUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="partner-marketing-open-landing"
            className="inline-flex items-center px-3 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm"
          >
            <ExternalLink className="w-4 h-4 mr-1" /> Preview
          </a>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        Share this URL on social media, email signatures, business cards, or your Google Business
        Profile. It personalises with your name, region and headshot once your landing page is live.
      </p>
    </section>
  );
}


// ── Region label + hero intro copy (partner self-serve) ─────────
// Persists to `region_label` and `hero_intro_override` on the
// partner's landing-page row. Admin still owns everything else
// (photo overrides, phone, email, franchisee_name, status).
function PartnerLandingContentCard({ partner }) {
  const [regionLabel, setRegionLabel] = useState("");
  const [heroIntro, setHeroIntro] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Load current values.
  useEffect(() => {
    if (!partner?.partner_slug) { setLoading(false); return; }
    let cancelled = false;
    api.get(`/pws-partner-landing/by-partner-slug/${encodeURIComponent(partner.partner_slug)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setRegionLabel(data?.region_label || "");
        setHeroIntro(data?.hero_intro_override || "");
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [partner?.partner_slug]);

  const HERO_MAX = 800;
  const REGION_MAX = 140;

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch("/partner/landing-page/content", {
        region_label:        regionLabel,
        hero_intro_override: heroIntro,
      });
      setDirty(false);
      toast.success("Landing page copy updated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
      data-testid="partner-marketing-content-card"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
          <FileText className="w-4 h-4 text-cyan-600" /> Your landing page copy
        </h2>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          Edit any time — goes live instantly
        </span>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
            Region banner
          </label>
          <input
            data-testid="partner-marketing-region-input"
            value={regionLabel}
            disabled={loading || saving}
            maxLength={REGION_MAX}
            onChange={(e) => { setRegionLabel(e.target.value); setDirty(true); }}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50 disabled:text-slate-400"
            placeholder="e.g. Sunshine Coast, QLD"
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Shown in the top bar (&ldquo;Proudly supporting homeowners in&hellip;&rdquo;) and header wordmark.
          </p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
            Hero intro override (optional)
          </label>
          <textarea
            data-testid="partner-marketing-hero-input"
            value={heroIntro}
            disabled={loading || saving}
            maxLength={HERO_MAX}
            onChange={(e) => { setHeroIntro(e.target.value); setDirty(true); }}
            rows={3}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400"
            placeholder="Leave blank to use the standard AWARE intro. Tell homeowners why you're the right local water testing partner."
          />
          <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
            <span>Rich stories build trust. Aim for 2&ndash;3 short sentences.</span>
            <span className="tabular-nums">{heroIntro.length}/{HERO_MAX}</span>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          data-testid="partner-marketing-content-save"
          onClick={onSave}
          disabled={!dirty || saving || loading}
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {saving ? "Saving\u2026" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>
    </section>
  );
}


// ── Franchisee headshot uploader ──────────────────────────────────
// Persists to `franchisee_photo_url` on the partner's landing-page
// row via `POST /api/partner/landing-page/photo`. Same photo powers
// the round headshot on the customer-facing landing page.
function FranchiseeHeadshotCard({ partner }) {
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  // Prime the current photo (if any) so the partner sees what
  // customers currently see.
  useEffect(() => {
    if (!partner?.partner_slug) { setInitLoading(false); return; }
    let cancelled = false;
    api.get(`/pws-partner-landing/by-partner-slug/${encodeURIComponent(partner.partner_slug)}`)
      .then(({ data }) => { if (!cancelled) setPhotoUrl(data?.franchisee_photo_url || ""); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setInitLoading(false); });
    return () => { cancelled = true; };
  }, [partner?.partner_slug]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Please upload a JPG, PNG or WebP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/partner/landing-page/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(data.franchisee_photo_url);
      toast.success("Headshot updated — customers will see the new photo on your landing page.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      // Reset the input so the same file can be re-selected.
      if (e.target) e.target.value = "";
    }
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="partner-marketing-headshot-card"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
          <Camera className="w-4 h-4 text-cyan-600" /> Your headshot
        </h2>
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          Shown on your customer landing page
        </span>
      </div>
      <div className="mt-4 flex items-center gap-5 flex-wrap">
        <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
          {initLoading ? (
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
          ) : photoUrl ? (
            <img
              src={photoUrl}
              alt="Your headshot"
              className="w-full h-full object-cover"
              data-testid="partner-marketing-headshot-preview"
            />
          ) : (
            <Camera className="w-8 h-8 text-slate-400" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2 text-sm">
          <p className="text-slate-600 leading-relaxed">
            A friendly headshot builds trust. Upload a clear front-facing photo — square works best.
            The image appears in the &ldquo;Your Local Water Specialists&rdquo; card on your landing page.
          </p>
          <label
            data-testid="partner-marketing-headshot-upload"
            className={`inline-flex items-center gap-1.5 rounded-md text-sm font-semibold px-4 py-2 cursor-pointer transition-colors ${
              uploading
                ? "bg-slate-200 text-slate-500 cursor-wait"
                : "bg-cyan-700 hover:bg-cyan-800 text-white"
            }`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading&hellip;
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> {photoUrl ? "Replace headshot" : "Upload headshot"}
              </>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={onFile}
              disabled={uploading}
            />
          </label>
          <p className="text-[11px] text-slate-500">
            JPG, PNG or WebP · Max 5 MB. New photos go live immediately on your landing page.
          </p>
        </div>
      </div>
    </section>
  );
}
